import React from 'react'
import Avatar from 'components/Avatar'
import AvatarStack from 'modules/shares/components/AvatarStack'
import { ButtonUnstyled } from 'components/Button'
import { Flex } from 'components/Box'
import Tooltip from 'components/Tooltip'
import noUserAvatar from './noUserAvatar.svg'
import AddMoreButton from './AddMoreButton'
import styled, { ThemeProps, DefaultTheme, withTheme } from 'styled-components'
import { ShareMembershipInfoFragment } from 'gqlTypes'
import useUserProfile from 'modules/user/useUserProfile'
import { getNameOrEmailFromMembership } from 'modules/shares/utils'

const isPendingMembership = (
  status: ShareMembershipInfoFragment['inviteStatus']
) => status !== 'ACCEPTED'

interface MembershipAvatarStackProps {
  memberships?: ShareMembershipInfoFragment[]
  totalCount: number
  onClickAvatar?: () => void
  maxAvatars: number
  avatarSize: number
}

const UntypedButtonUnstyled = ButtonUnstyled as any

const MembershipsAvatarStack: React.FunctionComponent<
  MembershipAvatarStackProps & ThemeProps<DefaultTheme>
> = ({ memberships = [], onClickAvatar, avatarSize, ...props }) => {
  const { data: userData } = useUserProfile(true)

  return (
    <AvatarStack avatarSize={avatarSize} {...props}>
      {memberships.map(membership => (
        <Tooltip
          key={membership.identifier!}
          placement="bottom"
          spacing="8px"
          content={
            <span>
              {getNameOrEmailFromMembership(
                membership,
                userData?.me.identifier
              )}{' '}
              can {membership.privateAccessLevel.toLowerCase()}{' '}
              {isPendingMembership(membership.inviteStatus) && '(Pending)'}
            </span>
          }>
          <UntypedButtonUnstyled
            style={{
              opacity: isPendingMembership(membership.inviteStatus)
                ? '0.4'
                : '1',
            }}
            onClick={onClickAvatar}
            blacklist={['fill', 'small', 'loading']}>
            <Avatar
              src={
                membership.workspaceMembership?.user?.avatar?.small ||
                noUserAvatar
              }
              name={getNameOrEmailFromMembership(
                membership,
                userData?.me.identifier
              )}
              size={avatarSize}
            />
          </UntypedButtonUnstyled>
        </Tooltip>
      ))}
    </AvatarStack>
  )
}

const OtherMembersContainer = styled(Flex)<{ withMargin: boolean }>`
  margin-left: ${({ withMargin }) => (withMargin ? '8px' : 0)};
`

interface OtherMembersProps {
  userCanUpdateSettings?: boolean
  totalCount: number
  openShareSettingsModal: () => void
  maxAvatars: number
  memberships?: ShareMembershipInfoFragment[]
  avatarSize: number
  isPublic: boolean
}

const OtherMembersThemed: React.FunctionComponent<
  OtherMembersProps & ThemeProps<DefaultTheme>
> = ({
  userCanUpdateSettings,
  memberships,
  totalCount,
  openShareSettingsModal,
  maxAvatars,
  avatarSize,
  theme,
  isPublic,
}) => (
  <OtherMembersContainer withMargin={isPublic}>
    {userCanUpdateSettings ? (
      <>
        <MembershipsAvatarStack
          memberships={memberships}
          totalCount={totalCount}
          maxAvatars={maxAvatars - 1} // Take in consideration the AddMoreButton
          onClickAvatar={openShareSettingsModal}
          avatarSize={avatarSize}
          theme={theme}
        />
        <AddMoreButton
          data-testid="add-more-members-button"
          onClick={openShareSettingsModal}
        />
      </>
    ) : (
      // Now we will be using Memberships for admins and viewers
      // More info: https://github.com/sketch-hq/Cloud/issues/6603#issuecomment-762899620
      <MembershipsAvatarStack
        memberships={memberships}
        totalCount={totalCount}
        maxAvatars={maxAvatars}
        avatarSize={avatarSize}
        theme={theme}
      />
    )}
  </OtherMembersContainer>
)

export default withTheme(OtherMembersThemed)
